import { computed, effect, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { Store } from '@ngrx/store';
import { MinimumSlideInformation } from '@pcp/core/interfaces';
import { fromClassroom } from 'shared/classroom/data-access';
import { getShowBasicSlidesPreview, setShowBasicSlidesPreview } from './navigation-preview-storage';

/** Number of slide thumbnails the navigation preview shows at once. */
export const PREVIEW_MAX_VISIBLE_COUNT = 2;

interface NavigationPreviewState {
    /** The basic slides preview is open (persisted per client in localStorage). */
    showBasicSlidesPreview: boolean;
    /** The extension slides preview is open. */
    showExtensionSlidesPreview: boolean;
    /** First basic slide shown in the preview (0...n-1). */
    basicSlidePreviewIdx: number | undefined;
    /** First extension slide shown in the preview (0...n-1), kept within `extensionSlides`. */
    extensionSlidePreviewIdx: number | undefined;
    extensionSlides: MinimumSlideInformation[] | undefined;
}

/**
 * This client's navigation preview (the beamer nav): which part is open, and which slides it shows.
 *
 * Client-local and never synced - every connected beamer has its own. Synced state reaches it
 * through `PreviewScreenEffects`: `onNavigationPreviewNextSlidesChanged$` resets the indices when
 * the teacher's slide context changes, `hideNavigationPreviewOnSlideShown$` closes the preview when
 * a slide is shown on any layer. The UI reads and drives it through `NextSlidesPreviewFacade`.
 */
export const NavigationPreviewStore = signalStore(
    { providedIn: 'root' },
    withState<NavigationPreviewState>(() => ({
        showBasicSlidesPreview: getShowBasicSlidesPreview(),
        showExtensionSlidesPreview: false,
        basicSlidePreviewIdx: undefined,
        extensionSlidePreviewIdx: undefined,
        extensionSlides: undefined,
    })),
    withComputed((state, store = inject(Store)) => {
        const isLessonPaused = store.selectSignal(fromClassroom.isLessonPaused);
        return {
            /** The basic slides preview is open and visible (a paused lesson hides it). */
            isShowingBasicSlidesPreview: computed(() => state.showBasicSlidesPreview() && !isLessonPaused()),
            extensionSlidePreviewNextEnabled: computed(() => {
                const length = state.extensionSlides()?.length ?? 0;
                const previewIdx = state.extensionSlidePreviewIdx();
                return length > 0 && previewIdx !== undefined
                    ? length - previewIdx > PREVIEW_MAX_VISIBLE_COUNT
                    : false;
            }),
            extensionSlidePreviewPreviousEnabled: computed(() => {
                const previewIdx = state.extensionSlidePreviewIdx();
                return (state.extensionSlides()?.length ?? 0) > 0 && previewIdx !== undefined && previewIdx > 0;
            }),
        };
    }),
    withMethods((state) => ({
        showBasicSlidesPreviewOnly(): void {
            patchState(state, { showBasicSlidesPreview: true, showExtensionSlidesPreview: false });
        },

        hideBasicSlidesPreview(): void {
            patchState(state, { showBasicSlidesPreview: false });
        },

        showPreviewForExtensionSlides(): void {
            patchState(state, { showExtensionSlidesPreview: true, showBasicSlidesPreview: false });
        },

        hidePreviewForExtensionSlides(): void {
            patchState(state, { showExtensionSlidesPreview: false });
        },

        /** Close the whole preview (basic and extension slides). */
        hide(): void {
            patchState(state, { showBasicSlidesPreview: false, showExtensionSlidesPreview: false });
        },

        showPreviewForPreviousBasicSlide(): void {
            const idx = state.basicSlidePreviewIdx();
            if (idx !== undefined) {
                patchState(state, { basicSlidePreviewIdx: idx - 1 });
            }
        },

        showPreviewForNextBasicSlide(): void {
            const idx = state.basicSlidePreviewIdx();
            if (idx !== undefined) {
                patchState(state, { basicSlidePreviewIdx: idx + 1 });
            }
        },

        showPreviewForPreviousExtensionSlide(): void {
            const idx = state.extensionSlidePreviewIdx();
            if (idx !== undefined) {
                patchState(state, { extensionSlidePreviewIdx: checkBounds(state.extensionSlides(), idx - 1) });
            }
        },

        showPreviewForNextExtensionSlide(): void {
            const idx = state.extensionSlidePreviewIdx();
            if (idx !== undefined) {
                patchState(state, { extensionSlidePreviewIdx: checkBounds(state.extensionSlides(), idx + 1) });
            }
        },

        setExtensionSlidePreviewIdx(extensionSlidePreviewIdx: number): void {
            patchState(state, {
                extensionSlidePreviewIdx: checkBounds(state.extensionSlides(), extensionSlidePreviewIdx),
            });
        },

        setBasicSlidePreviewIdx(basicSlidePreviewIdx: number): void {
            patchState(state, { basicSlidePreviewIdx });
        },

        /** Re-seed the indices from the teacher's synced slide context (see `PreviewScreenEffects`). */
        resetFromYjs(args: Pick<NavigationPreviewState,
            'basicSlidePreviewIdx' | 'extensionSlidePreviewIdx' | 'extensionSlides'>): void {
            patchState(state, args);
        },
    })),
    withHooks({
        onInit(state) {
            effect(() => setShowBasicSlidesPreview(state.showBasicSlidesPreview()));
        },
    }),
);

const checkBounds = (
    items: MinimumSlideInformation[] | undefined,
    previewIndex: number
): number | undefined => {
    if (!items) {
        return undefined;
    } else if (previewIndex >= items.length) {
        return items.length - 1;
    } else if (previewIndex < 0) {
        return 0;
    }
    return previewIndex;
};
